import React from 'react';
import { CalendarClockIcon, CheckSquareIcon, MailIcon, MessageSquareIcon, PhoneIcon, StickyNoteIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import type { Activity, ActivityType } from '../../types';
import { formatDuration, formatRelative } from '../../utils/format';
const icons: Record<ActivityType, React.ComponentType<{
  className?: string;
}>> = {
  call: PhoneIcon,
  sms: MessageSquareIcon,
  email: MailIcon,
  task: CheckSquareIcon,
  event: CalendarClockIcon,
  note: StickyNoteIcon
};
const labels: Record<ActivityType, string> = {
  call: 'Call',
  sms: 'SMS',
  email: 'Email',
  task: 'Task',
  event: 'Event',
  note: 'Note'
};
function sortKey(activity: Activity): number {
  if (activity.dueInMinutes !== undefined) return -Math.abs(activity.dueInMinutes);
  return activity.minutesAgo ?? 0;
}
export function ActivityTimeline({
  activities
}: {
  activities: Activity[];
}) {
  const ordered = [...activities].sort((a, b) => sortKey(a) - sortKey(b));
  return <Card className="p-6" data-id="element-120" data-mp="371v6shqw8idu">
      <div className="mb-5 flex items-baseline justify-between" data-id="element-121" data-mp="371br77opu62z">
        <h2 className="text-sm font-semibold text-ink-900" data-id="element-122" data-mp="3c17t91durnfw">Activity Timeline</h2>
        <span className="text-xs text-ink-500" data-id="element-123" data-mp="3c3api35lmct2">{activities.length} records</span>
      </div>

      <ol className="relative space-y-1 pl-1" data-id="element-124" data-mp="3gy7y58yyuk5f">
        <span className="absolute left-[15px] top-2 bottom-2 w-px bg-ink-200" aria-hidden="true" data-id="element-125" data-mp="3l2o3n94o3pk4" />
        {ordered.map(activity => {
        const Icon = icons[activity.type];
        const overdue = activity.dueInMinutes !== undefined && activity.dueInMinutes < 0;
        const scheduled = activity.dueInMinutes !== undefined && activity.dueInMinutes >= 0;
        return <li key={activity.id} className="relative flex gap-3.5 rounded-xl px-1 py-2.5" data-id="element-126" data-mp="8lix4nnjvquc">
              <span className={twMerge('relative z-10 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-4 ring-white', overdue ? 'bg-amber-100 text-amber-700' : 'bg-ink-100 text-ink-600')} data-id="element-127" data-mp="cn4fgoiusxbx">
                <Icon className="h-3.5 w-3.5" data-id="element-128" data-mp="2ig2e6sz2p6ke" />
              </span>
              <div className="min-w-0 flex-1" data-id="element-129" data-mp="2dokd84x1v5zf">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-130" data-mp="2dmr7cru9gpsh">
                  <p className={twMerge('text-sm font-medium', overdue ? 'text-amber-900' : 'text-ink-900')} data-id="element-131" data-mp="2sc5h411r198e">
                    {activity.title}
                  </p>
                  {overdue ? <Badge tone="warning" data-id="element-132" data-mp="2xdmh7ucesrnx">
                      Past Due {labels[activity.type]} ·{' '}
                      {formatDuration(activity.dueInMinutes ?? 0)} late
                    </Badge> : null}
                  {scheduled ? <Badge tone="neutral" data-id="element-133" data-mp="sulhgqifnj1s">
                      Due in {formatDuration(activity.dueInMinutes ?? 0)}
                    </Badge> : null}
                  {activity.outcome ? <Badge tone="neutral" data-id="element-134" data-mp="j4s9dzhi1m2a">{activity.outcome}</Badge> : null}
                </div>
                {activity.detail ? <p className="mt-1 text-sm leading-relaxed text-ink-500" data-id="element-135" data-mp="o2lgqvujgm45">{activity.detail}</p> : null}
                <p className="mt-1 text-xs text-ink-400" data-id="element-136" data-mp="94t3698jy7sl">
                  {labels[activity.type]} ·{' '}
                  {activity.minutesAgo !== undefined ? formatRelative(activity.minutesAgo) : overdue ? 'Scheduled, not completed' : 'Upcoming'}
                </p>
              </div>
            </li>;
      })}
      </ol>
    </Card>;
}